import {useCallback, useMemo} from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  RadioGroup,
  FormControlLabel,
  Radio,
  Grid,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  SeasonalDateRangeInput,
  ISeasonalDateRange,
} from './SeasonalDateRangeInput';
import {DayOfWeekSelector} from './DayOfWeekSelector';

interface SeasonalDateRangeSelectorProps {
  dateRanges: ISeasonalDateRange[];
  setDateRanges: React.Dispatch<React.SetStateAction<ISeasonalDateRange[]>>;
  dateType?: 'one-day' | 'multiple-days';
  setDateType?: (val: 'one-day' | 'multiple-days') => void;
}

export const SeasonalDateRangeSelector = ({
  dateRanges,
  setDateRanges,
  dateType = 'one-day',
  setDateType,
}: SeasonalDateRangeSelectorProps) => {
  const handleTypeChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newType = event.target.value as 'one-day' | 'multiple-days';
      if (setDateType) {
        setDateType(newType);
      }
    },
    [setDateType],
  );

  const handleAddRange = useCallback(
    (newRange: ISeasonalDateRange) => {
      setDateRanges(prev => {
        const updatedRanges = [...prev, newRange];
        // Sort by start_date ascending
        return updatedRanges.sort(
          (a, b) =>
            (a.start_date?.valueOf() || 0) - (b.start_date?.valueOf() || 0),
        );
      });
    },
    [setDateRanges],
  );

  const handleRemoveRange = useCallback(
    (id: number) => {
      setDateRanges(prev => prev.filter(range => range.id !== id));
    },
    [setDateRanges],
  );

  const handleDaysChange = useCallback(
    (rangeId: number, days: string[]) => {
      setDateRanges(prev =>
        prev.map(range =>
          range.id === rangeId ? {...range, days_of_operation: days} : range,
        ),
      );
    },
    [setDateRanges],
  );

  const columns = useMemo(() => {
    if (dateType === 'one-day') {
      return [
        {
          key: 'date',
          label: 'Date',
          render: (range: ISeasonalDateRange) =>
            range.start_date?.format('MM/DD/YYYY'),
        },
        {
          key: 'day',
          label: 'Day',
          render: (range: ISeasonalDateRange) =>
            range.start_date?.format('dddd'),
        },
      ];
    } else {
      return [
        {
          key: 'startDate',
          label: 'Start Date',
          render: (range: ISeasonalDateRange) =>
            range.start_date?.format('MM/DD/YYYY'),
        },
        {
          key: 'endDate',
          label: 'End Date',
          render: (range: ISeasonalDateRange) =>
            range.end_date?.format('MM/DD/YYYY'),
        },
        {
          key: 'daysOfOperation',
          label: 'Days of Operation',
          align: 'center' as const,
          render: (range: ISeasonalDateRange) => (
            <DayOfWeekSelector
              rangeId={range.id}
              startDate={range.start_date!}
              endDate={range.end_date!}
              selectedDays={range.days_of_operation}
              onChange={handleDaysChange}
            />
          ),
        },
      ];
    }
  }, [dateType, handleDaysChange]);

  // Calculate message parts based on dateType
  const isOneDay = dateType === 'one-day';
  const itemTypePlural = isOneDay ? 'individual dates' : 'date ranges';
  const pickerType = isOneDay ? 'date picker' : 'date range picker';
  const itemTypeSingular = isOneDay ? 'date' : 'range';

  // Date type options configuration
  const dateTypeOptions = useMemo(
    () => [
      {
        value: 'one-day' as const,
        label: 'One Day',
        caption: 'Choose multiple individual dates',
      },
      {
        value: 'multiple-days' as const,
        label: 'Multiple Days',
        caption: 'Choose multiple date ranges',
      },
    ],
    [],
  );

  // Filter ranges based on current date type
  const filteredRanges = useMemo(
    () =>
      dateRanges.filter(range => {
        if (!range.start_date || !range.end_date) return false;
        const isSingleDay = range.start_date.isSame(range.end_date, 'day');
        return dateType === 'one-day' ? isSingleDay : !isSingleDay;
      }),
    [dateRanges, dateType],
  );

  return (
    <Box>
      <Typography className='font-bold text-16 mb-2'>
        Select Date Type
      </Typography>
      <RadioGroup
        className='mb-4'
        value={dateType}
        onChange={handleTypeChange}
        row>
        <Grid container spacing={2}>
          {dateTypeOptions.map(option => (
            <Grid item xs={6} key={option.value}>
              <FormControlLabel
                className='flex items-start'
                value={option.value}
                control={<Radio className='py-1 pr-3' />}
                label={
                  <>
                    {option.label}
                    <Typography
                      variant='caption'
                      className='text-white-50 block'>
                      {option.caption}
                    </Typography>
                  </>
                }
              />
            </Grid>
          ))}
        </Grid>
      </RadioGroup>

      <SeasonalDateRangeInput
        existingRanges={dateRanges}
        onAdd={handleAddRange}
        isOneDay={dateType === 'one-day'}
      />

      {filteredRanges.length > 0 ? (
        <TableContainer className='my-3 max-h-400 overflow-auto scrollbar'>
          <Table>
            <TableHead className='blue-variant17'>
              <TableRow>
                {columns.map(col => (
                  <TableCell
                    key={col.key}
                    align={col.align}
                    className='py-6px px-16px'>
                    {col.label}
                  </TableCell>
                ))}
                <TableCell align='center' className='py-6px px-16px'>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRanges.map(range => {
                  if (range.start_date && range.end_date) {
                    return (
                      <TableRow key={range.id}>
                        {columns.map(col => (
                          <TableCell
                            key={col.key}
                            align={col.align}
                            className='py-6px px-16px'>
                            {col.render(range)}
                          </TableCell>
                        ))}
                        <TableCell align='center' className='py-6px px-16px'>
                          <IconButton
                            edge='end'
                            aria-label='delete'
                            onClick={() => handleRemoveRange(range.id)}
                            color='error'
                            size='small'>
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  }
                  return null;
                })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box className='p-4 text-center'>
          <Typography className='text-14 text-white-50'>
            No {itemTypePlural} added yet. Use the {pickerType} above to add
            your first {itemTypeSingular}.
          </Typography>
        </Box>
      )}
    </Box>
  );
};
